/**
 * The declarative derivation grammar — one small, pure evaluator that replaces three separate pockets
 * of hand-written behavior in the runner:
 *
 *   1. `config/liveFigures.ts` — per-gap-row functions that read `insights`/`installBase` and return a
 *      one-line "current state" string.
 *   2. report token fills — `[Bracketed]` substitutions across the report template.
 *   3. chart-dataset derivations — numbers computed off the same state surface.
 *
 * All three read the same runtime surface (workshop `state`, `insights`, `installBase`, answers, …) and
 * emit a formatted scalar. Expressing them as data — `{ source, format, template, fallback }` — lets a
 * campaign author derived figures, token values, and chart inputs entirely from the DB, with zero code:
 *
 *   source:   candidate dot-paths into the scope, evaluated FIRST-WINS (first non-nullish value used).
 *   format:   how to render the resolved value — 'text' | 'count' | 'percent' | 'ratio' | 'bool' |
 *             'template'.
 *   template: for format 'template', a string with `{value}` (the resolved value) and `{some.path}`
 *             tokens resolved from the scope; unmatched brackets survive verbatim (like `applyTokens`).
 *   fallback: used when NO source path resolves (all nullish) — a literal string, or for 'template' the
 *             template is still rendered with the fallback as `{value}`.
 *
 * The evaluator is deliberately pure and dependency-free so it is trivially testable and can run in the
 * runner, in report assembly, and in chart building alike.
 */

export type DerivationFormat = 'text' | 'count' | 'percent' | 'ratio' | 'bool' | 'template';

export interface Derivation {
  /**
   * Candidate paths into the scope; the first that resolves to a non-nullish value wins. Paths are
   * dot-separated and additionally support bracket segments so keys that themselves contain a dot are
   * addressable, e.g. `state.discoveryAnswers['2-rec.inventory-tools']` or `insights.tools[1]`.
   */
  source?: string[];
  /** How to render the resolved value. Defaults to 'text'. */
  format?: DerivationFormat;
  /** For 'template': the string to fill. `{value}` = resolved value; `{a.b}` = scope path. */
  template?: string;
  /** Rendered when no source path resolves. */
  fallback?: string;
  /**
   * When true, an empty or whitespace-only string counts as "missing" for source resolution — the path
   * is skipped as if null. Mirrors the hand-written `(answers[k] ?? '').trim() || fallback` idiom, where
   * a blank SE answer must fall through to the next source / fallback rather than render as empty.
   */
  skipEmpty?: boolean;
  /** For 'bool': label for a truthy value (default 'Yes'). */
  trueLabel?: string;
  /** For 'bool': label for a falsy (but present) value (default 'No'). */
  falseLabel?: string;
}

/** The runtime surface a derivation reads. Arbitrary nested plain data (state, insights, …). */
export type DerivationScope = Record<string, unknown>;

/**
 * Split a path into segments, honouring both dot notation and bracket notation so keys that contain a
 * dot survive intact. `state.discoveryAnswers['2-rec.inventory-tools']` → `['state',
 * 'discoveryAnswers', '2-rec.inventory-tools']`; `insights.tools[1]` → `['insights', 'tools', '1']`.
 * Quotes around a bracket key (single or double) are stripped.
 */
export function tokenizePath(path: string): string[] {
  const segments: string[] = [];
  let buf = '';
  let i = 0;
  const flush = () => {
    if (buf !== '') segments.push(buf);
    buf = '';
  };
  while (i < path.length) {
    const ch = path[i];
    if (ch === '.') {
      flush();
      i += 1;
    } else if (ch === '[') {
      flush();
      const end = path.indexOf(']', i);
      if (end === -1) {
        // Unterminated bracket — treat the remainder as a literal key.
        segments.push(path.slice(i + 1));
        return segments;
      }
      let key = path.slice(i + 1, end).trim();
      if (
        (key.startsWith("'") && key.endsWith("'")) ||
        (key.startsWith('"') && key.endsWith('"'))
      ) {
        key = key.slice(1, -1);
      }
      segments.push(key);
      i = end + 1;
    } else {
      buf += ch;
      i += 1;
    }
  }
  flush();
  return segments;
}

/** Resolve a path (dot + bracket notation, numeric array indices) into `scope`, or undefined. */
export function resolvePath(scope: unknown, path: string): unknown {
  if (!path) return undefined;
  let cur: unknown = scope;
  for (const seg of tokenizePath(path)) {
    if (cur == null) return undefined;
    if (Array.isArray(cur)) {
      const idx = Number(seg);
      cur = Number.isInteger(idx) ? cur[idx] : undefined;
    } else if (typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[seg];
    } else {
      return undefined;
    }
  }
  return cur;
}

/** True when a value should count as "missing" for source resolution. */
function isMissing(value: unknown, skipEmpty: boolean): boolean {
  if (value == null) return true;
  if (skipEmpty && typeof value === 'string' && value.trim() === '') return true;
  return false;
}

/** The first source path that resolves to a present value, else undefined. */
function firstResolved(
  scope: DerivationScope,
  sources: string[] | undefined,
  skipEmpty: boolean,
): unknown {
  for (const path of sources ?? []) {
    const v = resolvePath(scope, path);
    if (!isMissing(v, skipEmpty)) return v;
  }
  return undefined;
}

/** Fill `{value}` and `{some.path}` tokens; unmatched `[..]`/`{..}` survive verbatim. */
function fillTemplate(template: string, value: unknown, scope: DerivationScope): string {
  return template.replace(/\{([^{}]+)\}/g, (whole, token: string) => {
    const key = token.trim();
    if (key === 'value') return value == null ? '' : String(value);
    const resolved = resolvePath(scope, key);
    return resolved == null ? whole : String(resolved);
  });
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

/**
 * Evaluate a derivation against a scope, returning the formatted string.
 *
 * - 'text'     → the value coerced to string.
 * - 'count'    → array length, or the number, as an integer string.
 * - 'percent'  → `${round(n)}%` (n treated as an already-scaled 0–100 percentage; a 0–1 fraction is
 *                scaled up so both authoring conventions work).
 * - 'ratio'    → `${a}/${b}` from a 2-tuple / `{n,d}` / `{numerator,denominator}` value.
 * - 'bool'     → `trueLabel` / `falseLabel` (default 'Yes' / 'No'); a present-but-falsy value renders
 *                `falseLabel`, only an absent value renders the fallback.
 * - 'template' → `template` filled from `{value}` + scope paths.
 */
export function evaluateDerivation(derivation: Derivation, scope: DerivationScope): string {
  const { format = 'text', template, fallback, skipEmpty = false } = derivation;
  const value = firstResolved(scope, derivation.source, skipEmpty);

  if (format === 'template') {
    // No resolved value AND no fallback → the empty string, rather than a template rendered with an
    // empty `{value}`. This lets a derivation whose whole purpose is "render this phrasing when the
    // figure exists, otherwise nothing" collapse to empty when absent (a caller can then treat empty as
    // "defer" — e.g. a live gap-row figure that yields the SE-authored text instead). When a fallback IS
    // given it is still rendered as `{value}`, unchanged.
    if (value == null && fallback == null) return '';
    const base = value != null ? value : fallback ?? '';
    return template ? fillTemplate(template, base, scope) : String(base);
  }

  if (value == null) return fallback ?? '';

  switch (format) {
    case 'count': {
      if (Array.isArray(value)) return String(value.length);
      const n = toNumber(value);
      return n == null ? String(value) : String(Math.round(n));
    }
    case 'percent': {
      const n = toNumber(value);
      if (n == null) return fallback ?? String(value);
      const pct = n > 0 && n <= 1 ? n * 100 : n;
      return `${Math.round(pct)}%`;
    }
    case 'ratio': {
      if (Array.isArray(value) && value.length >= 2) return `${value[0]}/${value[1]}`;
      if (value && typeof value === 'object') {
        const o = value as Record<string, unknown>;
        const num = o.n ?? o.numerator;
        const den = o.d ?? o.denominator;
        if (num != null && den != null) return `${num}/${den}`;
      }
      return String(value);
    }
    case 'bool':
      return value ? derivation.trueLabel ?? 'Yes' : derivation.falseLabel ?? 'No';
    case 'text':
    default:
      return String(value);
  }
}
